import { defineStore } from "pinia";
import { request, requestBool } from "@/api-connectors/basicRequests";
import { DateTime } from "luxon";

import { useLocationStore } from "@/stores/locations";
import { useUserStore } from "@/stores/user";

import convertFormat, { isExpired } from "@/functions/luxon";
import { mapNotificationTopic } from "@/functions/converter";

import {
    InformationOptionLocation,
    News,
    Notification,
    ProductPresentation,
    Promotion,
    PromotionOld,
    TrackingParams
} from "@/types/notification";
import { PaginatedList } from "@/types/store";
import { DATE_FORMAT } from "@/types/enum/luxonEnum";
import { PERMISSION_KEY } from "@/types/user";
import { INFORMATION_TYPE, NotificationTopic, STAKEHOLDER_KIND } from "@/types/enum/notification";
import { ProductMaster} from "@/types/supplier";

export interface NotificationStore {
    notifications: PaginatedList<Notification>;
    news: PaginatedList<News>;
    productSuggestion: PaginatedList<ProductPresentation>;
    promotions: PaginatedList<Promotion>;
    oldPromotions: PromotionOld[];
    isLoaded: {
        [key: string]: boolean,
    },
    lastSkip: number;
    currentInformationIndex: number
}

export const useNotificationStore = defineStore('notification', {
    state: (): NotificationStore => {
        return {
            notifications: new PaginatedList<Notification>([]),
            news: new PaginatedList<News>([]),
            productSuggestion: new PaginatedList<ProductPresentation>([]),
            isLoaded: {},
            lastSkip: 0,
            promotions: new PaginatedList<Promotion>([]),
            oldPromotions: [],
            currentInformationIndex: 0
        }
    },
    actions: {
        clearStateVariables(): void {
            this.promotions.data = []
        },

        async getNotifications(skip?: number, take?: number): Promise<boolean> {
            const response = await request<PaginatedList<Notification>>({
                method: 'GET',
                route: 'notifications',
                query: { skip: skip, take: take }
            })

            if (!response) return false

            this.notifications = response
            this.isLoaded.notifications = true
            return true
        },

        async getNews(params: InformationOptionLocation): Promise<boolean> {
            const response = await request<PaginatedList<News>>({
                method: 'GET',
                route: `locations/${params.locationId}/feed`,
                query: { skip: params.skip, take: params.take, kind: INFORMATION_TYPE.NEWS, sortDesc: true }
            })

            if (!response) return false
            this.news = response
            this.isLoaded.news = true
            return true
        },

        async getSingleNews(informationId: string, locationId: string, type: NotificationTopic): Promise<News | undefined> {
            if (!informationId) return

            let kind: INFORMATION_TYPE
            try {
                kind = mapNotificationTopic(type)
            } catch (e) {
                console.error(e)
                return
            }

            const response = await request<News>({
                method: 'GET',
                route: `locations/${locationId}/feed/${informationId}`,
                query: { kind }
            })

            if (!response) requestBool({
                method: 'PUT',
                route: `notifications/${informationId}/read`,
                body: {
                    isRead: true
                }
            })

            return response
        },

        async getProductSuggestion(params: InformationOptionLocation): Promise<boolean> {
            const response = await request<PaginatedList<ProductPresentation>>({
                method: 'GET',
                route: `locations/${params.locationId}/feed`,
                query: { skip: params.skip, take: params.take, kind: INFORMATION_TYPE.PRODUCT_PRESENTATION }
            })

            if (!response) return false

            this.productSuggestion = response
            this.isLoaded.productSuggestion = true
            return true
        },

        async getPromotions(params: InformationOptionLocation): Promise<boolean> {
            if (!params) return false
            if (!params.take) params.take = 0 // set limit=0 to avoid default 50-item response limit (prevent BE-default limit of 50)

            const response = await request<PaginatedList<Promotion>>({
                method: 'GET',
                route: `locations/${params.locationId}/feed`,
                query: { skip: params.skip, limit: params.take, kind: INFORMATION_TYPE.PROMOTION }
            })

            if (!response) return false

            this.promotions = response
            this.isLoaded.promotions = true
            return true
        },

        async getPromotionsOld(locationId?: string): Promise<boolean> {
            const locationStore = useLocationStore()

            if (!locationStore.activeLocation?.id) return false

            const response = await request<PromotionOld[]>({
                method: 'GET',
                route: `locations/${locationId ?? locationStore.activeLocation.id}/promotions`
            })

            if (!response) return false

            this.oldPromotions = response
            this.isLoaded.promotions = true
            return true
        },

        async setRead(notification: Notification): Promise<boolean> {
            if (!notification.id) return false;

            const response = requestBool({
                method: 'PUT',
                route: `notifications/${notification.id}/read`,
                body: {
                    isRead: true
                }
            })

            if (!response) return false

            await this.getNotifications()

            return true
        },

        async getInformation<T extends INFORMATION_TYPE>(type: T, options: InformationOptionLocation): Promise<boolean> {
            if (type === INFORMATION_TYPE.NEWS) return this.getNews(options)

            if (type === INFORMATION_TYPE.PRODUCT_PRESENTATION)
                return this.getProductSuggestion(options)
            if (type === INFORMATION_TYPE.PROMOTION) return this.getPromotions(options)

            return false
        },

        async sendTrackingData(params: TrackingParams) {
            const { locationId, informationId, kind, action } = params
            if (!informationId || !locationId) return
            return requestBool({
                method: 'POST',
                route: `locations/${locationId}/feed/${informationId}/track`,
                query: { kind },
                body: { action }
            })
        },

        setPromotionIndex(id: string): void {
            const promotionIndex = this.promotionSortByExpiration.findIndex((item) => item.id === id)

            if (promotionIndex === -1) {
                const productIndex = this.oldPromotions.findIndex((item) => item.id === id)
                this.currentInformationIndex = this.promotionSortByExpiration.length + productIndex
            } else this.currentInformationIndex = promotionIndex
        },

        setProductSuggestionIndex(id: string): void {
            const productSuggestionIndex = this.productSuggestion.data.findIndex((item) => item.id === id)
            if (productSuggestionIndex !== -1) this.currentInformationIndex = productSuggestionIndex
        },

        findPromotion(): Promotion {
            return this.promotionSortByExpiration[this.currentInformationIndex]
        },

        findProduct(): PromotionOld {
            return this.oldPromotions[this.currentInformationIndex - this.promotionSortByExpiration.length]
        },

        findProductSuggestion(): ProductPresentation {
            return this.productSuggestion.data[this.currentInformationIndex]
        },

        findPromotionProduct(promotionId?: string, productId?: string): ProductMaster | undefined {
            return this.promotions.data.find((promotion) => promotion.id === promotionId)?.products.find((product) => product.id === productId)
        },
    },
    getters: {
        groupByDate(state): (locationId?: string) => Record<string, Notification[]> {
            return (locationId?: string): Record<string, Notification[]> => {
                const notificationGroups: Record<string, Notification[]> = {}
                const sortedArray: Notification[] = state.notifications.data.sort((a, b) => {
                    return DateTime.fromISO(b.createdAt ?? '').toMillis() - DateTime.fromISO(a.createdAt ?? '').toMillis()
                })

                for (const notification of sortedArray) {
                    if (!notification.createdAt) continue
                    if (!!locationId && !!notification.meta.locationId && notification.meta.locationId !== locationId) continue

                    const date = convertFormat(notification.createdAt, 'iso', DATE_FORMAT.DE_SHORT)

                    if (!notificationGroups[date]) notificationGroups[date] = []

                    notificationGroups[date].push(notification)
                }

                return notificationGroups
            }
        },

        getOctopusPromotions(state): Promotion[] {
            return state.promotions.data.filter(promotion => promotion.initiator.kind === STAKEHOLDER_KIND.OCTOPUS)
        },

        getDelivererPromotions(state): Promotion[] {
            return state.promotions.data.filter(promotion => promotion.initiator.kind !== STAKEHOLDER_KIND.OCTOPUS)
        },

        getAllPromotions(state): Promotion[] {
            return state.promotions.data;
        },

        onlyNews(state): News[] {
            return state.news.data?.filter(
                news => news.kind === INFORMATION_TYPE.NEWS /*&&
                    !isExpired(DateTime.fromISO(news.validUntil ?? news.validFrom), { value: -1, unit: 'weeks' })*/
            ) ?? []
        },

        unreadNotifications(state): (locationId?: string) => Notification[] {
            return (locationId?: string): Notification[] => {
                const value = state.notifications.data.filter(notification => unreadLocationNotification(notification, locationId))
                if (!useUserStore().permission[PERMISSION_KEY.ORDER_HISTORY_READ])
                    return value.filter(notification => notification.topic !== NotificationTopic.ORDER)
                return value
            }
        },

        homeNotifications(state): (locationId?: string) => Notification[] {
            return (locationId?: string): Notification[] => {
                return state.notifications.data?.filter(notification =>
                    unreadLocationNotification(notification, locationId) &&
                    !isExpired(DateTime.fromISO(notification.createdAt ?? ''), { value: -1, unit: 'weeks' })
                )?.slice(0, 10) ?? []
            }
        },

        promotionSortByExpiration(state): Promotion[] {
            return state.promotions.data.sort((a: Promotion, b: Promotion) => {
                if (!a.validUntil && !b.validUntil) return 0
                if (!a.validUntil) return 1
                if (!b.validUntil) return -1
                return DateTime.fromISO(a.validUntil ?? '').toMillis() - DateTime.fromISO(b.validUntil ?? '').toMillis()
            })
        },

        hasPromotions(state): boolean {
            return state.promotions?.data?.length > 0 || state.oldPromotions?.length > 0
        },

        allPromotions(state): PromotionList[] {
            const newPromotionList = state.promotions.data.map<PromotionListNew>((promotion: Promotion) => {
                return {
                    type: 'new',
                    item: promotion
                }
            })

            const oldPromotionList = state.oldPromotions.map<PromotionListOld>((promotion: PromotionOld) => {
                return {
                    type: 'old',
                    item: promotion
                }
            })

            return [...newPromotionList, ...oldPromotionList]
        },

        currentPromotion(state): PromotionList | undefined {
            return this.allPromotions[state.currentInformationIndex]
        }
    }
})

type PromotionList = PromotionListNew | PromotionListOld

interface PromotionListNew {
    type: 'new';
    item: Promotion
}

interface PromotionListOld {
    type: 'old';
    item: PromotionOld
}

const unreadLocationNotification = (notification: Notification, locationId?: string): boolean => {
    if (notification.isRead) return false
    if (!locationId) return true
    if (!notification.meta.locationId) return true
    return locationId === notification.meta.locationId
}
